import { TokenHoldingsDropdown } from './TokenHoldingsDropdown';
import { TokenAssetAllocationBreakdown } from './TokenAssetAllocationBreakdown';
import { TokenHoldingsOverviewSkeleton } from './TokenHoldingsOverview.skeleton';
import { TokenHoldingsOverviewError } from './TokenHoldingsOverview.error';
import { TokenHoldingsOverviewEmpty } from './TokenHoldingsOverview.empty';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { blockchain } from 'src/api/blockchain';
import { useAddressAndChainParams } from 'src/hooks/routing/useAddressAndChainParams';
import { useMemo } from 'react';

export const TokenHoldingsOverview = () => {
  const { chain, address } = useAddressAndChainParams();

  const {
    data: { data: { tokens = [], price_percentage_breakdown = [] } = {} } = {},
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['evmTokensBalance', chain, address],
    queryFn: () => blockchain.getEVMTokensBalance(chain, address),
    enabled: !!chain && !!address,
  });

  const nonEthTokens = useMemo(
    () => tokens.filter((token) => token.symbol !== 'ETH'),
    [tokens],
  );

  if (isLoading) {
    return <TokenHoldingsOverviewSkeleton />;
  }

  if (error) {
    return (
      <TokenHoldingsOverviewError
        error={error}
        onRetry={() => refetch()}
        isRetrying={isRefetching}
      />
    );
  }

  if (!tokens.length || !nonEthTokens.length) {
    return (
      <TokenHoldingsOverviewEmpty
        onRetry={() => refetch()}
        isRetrying={isRefetching}
      />
    );
  }

  return (
    <Card className="mx-auto w-full max-w-[1500px] rounded-lg border border-muted-50 bg-background bg-white shadow-[0_0_10px_rgba(0,0,0,0.1)] dark:border-muted-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium uppercase text-muted-foreground">
            Token Holdings
          </CardTitle>
          <Badge variant="outline">{nonEthTokens.length} Token Contracts</Badge>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex gap-6">
          <div className="flex-shrink-0">
            <TokenHoldingsDropdown tokens={tokens} />
          </div>
          <div className="flex-1">
            <TokenAssetAllocationBreakdown
              breakdown={price_percentage_breakdown}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
